import { Trophy, Users, Plus } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface League {
  id: string
  name: string
  owner_id: string
  is_private: boolean
  draft_at?: string
  starters_count: number
  bench_count: number
  max_teams: number
  created_at: string
  updated_at: string
  participants_count?: number
}

interface YourLeaguesProps {
  leagues: League[]
  onCreateLeague?: () => void
  onJoinLeague?: () => void
  onViewLeague?: (leagueId: string) => void
}

export function YourLeagues({ 
  leagues, 
  onCreateLeague, 
  onJoinLeague,
  onViewLeague 
}: YourLeaguesProps) {
  if (leagues.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Your Leagues
          </CardTitle>
          <CardDescription>
            Join or create leagues to compete with other players
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
            <div>
              <p className="text-muted-foreground mb-2">You're not in any leagues yet</p>
              <p className="text-sm text-muted-foreground">
                Join existing leagues or create your own to start competing!
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={onCreateLeague}
                className="bg-primary hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create League
              </Button>
              <Button 
                variant="outline" 
                onClick={onJoinLeague}
              >
                <Users className="h-4 w-4 mr-2" />
                Join League
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Your Leagues
            </CardTitle>
            <CardDescription>
              <span className="text-primary">View All Leagues</span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {leagues.map((league) => (
            <Card 
              key={league.id} 
              className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => onViewLeague?.(league.id)}
            >
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{league.name}</h3>
                    <Badge variant="outline">Fantasy Football</Badge>
                  </div>
                  
                   <div className="flex items-center gap-4 text-sm text-muted-foreground">
                     <div>Teams: {league.participants_count || 0}/{league.max_teams}</div>
                     <div>Next: {league.draft_at ? `Draft ${new Date(league.draft_at).toLocaleDateString()}` : 'Active'}</div>
                   </div>
                </div>

                <div className="text-right">
                  <div className="font-bold text-lg">{league.starters_count}+{league.bench_count}</div>
                  <div className="text-sm text-muted-foreground">
                    roster size
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}